import {
  Component,
  inject,
  signal,
  OnDestroy,
  QueryList,
  ViewChildren,
  ElementRef,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { timeout } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { AuthService } from '../../services/auth';
import { PartnerKycService } from '../../services/partner-kyc';
import { MenuService } from '../../services/menu';
import { ThemeService } from '../../services/theme';
import { ToastService } from '../../services/toast';

type LoginMode = 'password' | 'otp';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, DialogModule, RouterLink],
  templateUrl: './login.html',
  styleUrls: ['./login.css', './auth-hero.css'],
})
export class Login implements OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);
  private kycService = inject(PartnerKycService);
  private menuService = inject(MenuService);
  private themeService = inject(ThemeService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);



  // Signals so the zoneless app re-renders automatically (no manual detectChanges).
  mode = signal<LoginMode>('password');
  loading = signal(false);
  showPassword = signal(false);

  // ---- OTP modal state ----
  showOtpModal = signal(false);
  otpLoading = signal(false);
  resendLoading = signal(false);
  expired = signal(false);
  mobile = signal('');
  countdown = signal('05:00');

  private expiryMinutes = 5;
  private interval: any;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  loginForm: FormGroup = this.fb.group({
    username: ['', Validators.required],
    password: ['', Validators.required],
    rememberMe: [false],
  });

  otpForm: FormGroup = this.fb.group({
    otp: this.fb.array(
      Array.from({ length: 6 }, () =>
        this.fb.control('', { nonNullable: true, validators: Validators.required }),
      ),
    ),
  });

  get isOtpLogin() {
    return this.mode() === 'otp';
  }

  get otpArray(): FormArray<FormControl<string>> {
    return this.otpForm.get('otp') as FormArray<FormControl<string>>;
  }

  setMode(mode: LoginMode) {
    if (this.mode() === mode) return;
    this.mode.set(mode);

    const password = this.loginForm.get('password');
    if (mode === 'otp') {
      password?.clearValidators();
    } else {
      password?.setValidators([Validators.required]);
    }
    password?.updateValueAndValidity();
  }

  onLogin() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.toast.warning('Please fill in all required fields');
      return;
    }

    this.loading.set(true);
    const { username, password, rememberMe } = this.loginForm.value;

    if (this.isOtpLogin) {
      this.sendOtp(username);
    } else {
      this.passwordLogin(username, password, rememberMe);
    }
  }

  /**
   * After a successful login decide where to land:
   *  - Admins are never gated → straight to the app.
   *  - Partners: check KYC. Only `Approved` opens the app; anything else
   *    (NotSubmitted / Pending / Rejected) routes to the KYC page.
   */
  private postLogin() {
    // Drop any cached menu/KYC state from a previous session — without this, the guard and
    // sidebar can briefly run against the PREVIOUS user's menu if they never cleanly logged out.
    this.menuService.reset();
    this.kycService.reset();
    // The accent is stored against the account, and app-start init() ran before anyone was
    // signed in — so pull THIS user's theme now. It also corrects the case where the last
    // person on this browser used a different accent.
    this.themeService.syncFromServer();
    // Role-based landing (the '/dashboard' dispatcher guard would do the same — going
    // direct just skips the extra redirect hop).
    if (this.authService.isAdmin()) {
      this.router.navigate(['/admin/dashboard']);
      return;
    }
    // The timeout matters: without it a slow/hung KYC lookup left the user parked on the
    // login page forever after the "Login successful" toast. On timeout (or any failure)
    // we fall to the KYC page — the gate guard re-checks there and agrees.
    this.kycService.loadMyKyc().pipe(timeout(10_000)).subscribe({
      next: () => {
        this.router.navigate([this.kycService.isApproved() ? '/partner-dashboard' : '/partner-kyc']);
      },
      // If the lookup fails, send them to the KYC page (safe default — the gate guard agrees).
      error: () => this.router.navigate(['/partner-kyc']),
    });
  }

  // ================= PASSWORD LOGIN =================
  private passwordLogin(identifier: string, password: string, rememberMe: boolean) {
    this.authService.passwordLogin(identifier, password, rememberMe).subscribe({
      next: (res: any) => {
        if (res?.success === true) {
          // Keep the spinner on until navigation swaps the page out — releasing it here
          // made a slow post-login lookup read as "login did nothing".
          this.authService.saveAuth(res);
          this.toast.success('Login successful!');
          this.postLogin();
        } else {
          this.loading.set(false);
          this.toast.error(res?.message || 'Invalid username or password');
        }
      },
      error: (err: any) => {
        this.loading.set(false);
        // status 0 = network/server unreachable: the error interceptor already toasted
        // "Unable to reach the server" — a second "Invalid username or password" here
        // would blame the user's credentials for a connectivity problem.
        if (err?.status === 0) return;
        // 401/400 from the API carries the real reason ("Invalid password" / "User not found").
        this.toast.error(err?.error?.message || 'Invalid username or password');
      },
    });
  }

  // ================= OTP: SEND + OPEN MODAL =================
  private sendOtp(username: string) {
    this.authService.sendOtp(username).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success && res?.data?.otp_sent_to) {
          const mobile = String(res.data.otp_sent_to);
          this.mobile.set('******' + mobile.slice(-4));
          this.expiryMinutes = Number(res.data.expiry_minutes ?? 5) || 5;

          this.toast.success('OTP sent successfully!');
          this.openOtpModal();
        } else {
          this.toast.error(res?.message || 'Failed to send OTP');
        }
      },
      error: (err: any) => {
        this.loading.set(false);
        if (err?.status === 0) return; // interceptor already toasted the connectivity error
        this.toast.error(err?.error?.message || 'Failed to send OTP');
      },
    });
  }

  private openOtpModal() {
    this.otpArray.reset();
    this.expired.set(false);
    this.showOtpModal.set(true);
    this.startCountdown();
  }

  closeOtpModal() {
    this.showOtpModal.set(false);
    clearInterval(this.interval);
  }

  // Focus the first box once the dialog has rendered.
  onOtpModalShow() {
    this.otpInputs.get(0)?.nativeElement.focus();
  }

  // ================= OTP INPUT UX =================
  onInput(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    const digit = target.value.replace(/\D/g, '').slice(0, 1);
    this.otpArray.at(index).setValue(digit);
    target.value = digit;
    if (digit && index < 5) {
      this.otpInputs.get(index + 1)?.nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpArray.at(index).value && index > 0) {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (pasted.length === 6) {
      pasted.split('').forEach((val, i) => this.otpArray.at(i).setValue(val));
      this.otpInputs.get(5)?.nativeElement.focus();
    }
  }

  private getOtp(): string {
    return this.otpArray.value.join('');
  }

  // ================= OTP: VERIFY =================
  verifyOtp() {
    if (this.otpForm.invalid) {
      this.toast.warning('Please enter the complete 6-digit OTP');
      return;
    }

    const identifier = this.loginForm.get('username')?.value;
    const rememberMe = this.loginForm.get('rememberMe')?.value ?? false;
    this.otpLoading.set(true);
    const otp = Number(this.getOtp());

    this.authService.OTPLogin(identifier, otp, rememberMe).subscribe({
      next: (res: any) => {
        if (res?.success === true) {
          // Keep the spinner on until navigation swaps the page out (same as password login).
          this.authService.saveAuth(res);
          this.closeOtpModal();
          this.toast.success('Login successful!');
          this.postLogin();
        } else {
          this.otpLoading.set(false);
          this.toast.error(res?.message || 'Invalid or expired OTP');
        }
      },
      error: (err: any) => {
        this.otpLoading.set(false);
        if (err?.status !== 0) this.toast.error(err?.error?.message || 'Invalid or expired OTP');
        this.otpArray.reset();
        this.otpInputs.get(0)?.nativeElement.focus();
      },
    });
  }

  // ================= OTP: RESEND =================
  resendOtp() {
    if (this.resendLoading()) return;

    const identifier = this.loginForm.get('username')?.value;
    if (!identifier) {
      this.toast.warning('Username is missing. Please close and try again.');
      return;
    }

    this.resendLoading.set(true);

    this.authService.resendOtp(identifier).subscribe({
      next: (res: any) => {
        this.resendLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to resend OTP');
          return;
        }
        this.otpArray.reset();
        this.startCountdown();
        this.otpInputs.get(0)?.nativeElement.focus();
        this.toast.success('OTP resent successfully');
      },
      error: () => {
        this.resendLoading.set(false);
        // errorInterceptor already surfaces a toast
      },
    });
  }

  // ================= COUNTDOWN =================
  private startCountdown() {
    clearInterval(this.interval);
    this.expired.set(false);
    if (!isPlatformBrowser(this.platformId)) return;

    const endTime = Date.now() + this.expiryMinutes * 60 * 1000;

    const update = () => {
      const diff = endTime - Date.now();
      if (diff <= 0) {
        clearInterval(this.interval);
        this.countdown.set('00:00');
        // Keep the modal open so the user can resend — only mark it expired.
        this.expired.set(true);
        this.toast.warning('OTP expired. Tap "Resend OTP" to get a new code.');
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.countdown.set(
        `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      );
    };

    update();
    this.interval = setInterval(update, 1000);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }
}
